"use strict";
/**
 * Users canonical definition — the single source of truth for the `users` OpenSearch
 * index family.
 *
 * Per the canonical-shared-types model, `shared-types` (the pure, AWS-SDK-free contract
 * package) holds, per index family, BOTH:
 *   (a) the field-name constants (`USER_FIELDS`) — runtime values typed against
 *       `UserSearchDocument` keys so the contract and the constants cannot drift, and
 *   (b) the OpenSearch template body (`buildUserTemplate`) — settings (INCLUDING the
 *       `analysis` analyzers/normalizers), `dynamic: true`, and explicit `properties`.
 *
 * Every other surface (the reindex-script registrar, the search reader) references these
 * instead of authoring its own copy, so field names have exactly one source.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.USER_FIELDS = void 0;
exports.buildUserTemplate = buildUserTemplate;
/**
 * Canonical field-name constants for the `users` index family.
 *
 * Each value is a field name constrained to be a real key of `UserSearchDocument` via
 * `satisfies Record<string, keyof UserSearchDocument>`, so a typo or a rename that breaks
 * the contract fails to compile.
 */
exports.USER_FIELDS = {
    identity_id: 'identity_id',
    screen_name: 'screen_name',
    photo: 'photo',
    reputation_score: 'reputation_score',
    bio: 'bio',
};
/**
 * Build the composable OpenSearch index-template body for the `users` family.
 *
 * Ported VERBATIM from the reindex script's `defaultUsersTemplate`, including the full
 * `settings.analysis` block (the `fpw_text` analyzer, the `english_*` filters, and the
 * `lowercase` normalizer) which `screen_name` references. `mappings.dynamic` is the
 * literal `true` (the BF-018 guard). Property keys are keyed by the `USER_FIELDS`
 * constants so the mapping types exactly the fields the constants name.
 */
function buildUserTemplate(opts) {
    return {
        index_patterns: [opts.pattern],
        template: {
            settings: {
                number_of_shards: opts.shards ?? 1,
                number_of_replicas: opts.replicas ?? 0,
                refresh_interval: opts.refresh ?? '5s',
                analysis: {
                    analyzer: {
                        fpw_text: {
                            type: 'custom',
                            tokenizer: 'standard',
                            filter: ['lowercase', 'asciifolding', 'english_possessive_stemmer', 'english_stop', 'english_stemmer'],
                        },
                    },
                    filter: {
                        english_stop: { type: 'stop', stopwords: '_english_' },
                        english_stemmer: { type: 'stemmer', language: 'english' },
                        english_possessive_stemmer: { type: 'stemmer', language: 'possessive_english' },
                    },
                    normalizer: {
                        lowercase: { type: 'custom', char_filter: [], filter: ['lowercase', 'asciifolding'] },
                    },
                },
            },
            mappings: {
                dynamic: true,
                properties: {
                    [exports.USER_FIELDS.identity_id]: { type: 'keyword' },
                    [exports.USER_FIELDS.screen_name]: {
                        type: 'text',
                        analyzer: 'fpw_text',
                        fields: {
                            raw: { type: 'keyword', normalizer: 'lowercase' },
                            sasy: { type: 'search_as_you_type' },
                        },
                    },
                    [exports.USER_FIELDS.photo]: { type: 'keyword' },
                    [exports.USER_FIELDS.reputation_score]: { type: 'integer' },
                    [exports.USER_FIELDS.bio]: { type: 'text', analyzer: 'fpw_text' },
                },
            },
        },
        priority: 100,
        _meta: { managed_by: 'fpw', purpose: 'users' },
    };
}
